/**
 * Optimistic card moves for the candidate pipeline board.
 *
 * The cache write runs synchronously inside the drop event (see
 * `writeQueryDataSync`), so the reordered board lands in the same React commit
 * that ends the drag. dnd-kit then measures the card's new slot when it builds
 * the drop animation, instead of the slot the card just left.
 *
 * Only moves that apply straight away live here — stage changes that open a
 * confirmation dialog stay in `useCandidatePipelineMutations`.
 */
import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import type { Candidate } from '../models/job';
import {
  moveCandidatePipelineStage,
  updateCandidatePipelinePosition,
} from '../services/job.service';
import { jobKeys } from './useJobOffers';
import {
  applyOptimisticCardMove,
  type OptimisticCardMove,
} from '../utils/pipeline-cache';
import { writeQueryDataSync } from '../utils/query-sync-write';
import {
  applyActionFeedback,
  feedbackErrorMessage,
  feedbackForPipelineMove,
} from '../utils/pipeline-action-feedback';

const TOAST_PREFIX = 'candidates:notifications';

interface OptimisticContext {
  previousCandidates?: Candidate[];
}

interface MoveCardVariables extends OptimisticCardMove {
  toStageType: string;
  willNotify?: boolean;
  /** Set when the move is not visible to the user (Table); see feedbackForPipelineMove. */
  movedToStageLabel?: string;
}

interface ReorderCardVariables {
  candidateId: string;
  stageId: string;
  stageType?: string;
  insertAfterCandidateId: string | null;
}

export function useCandidatePipelineDnd(jobId: string) {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useTranslation(['candidates']);

  const queryKey = useMemo(() => jobKeys.candidatesForJob(jobId), [jobId]);
  const mutationKey = useMemo(
    () => ['candidate-pipeline-dnd', jobId] as const,
    [jobId]
  );

  /**
   * Stays synchronous on purpose: awaiting anything here would defer the write
   * past the drop and bring the snap-back animation back.
   */
  const applyOptimisticMove = useCallback(
    (move: OptimisticCardMove): OptimisticContext => {
      // `revert: false`: a reverting cancel would restore the pre-fetch
      // snapshot a microtask later and wipe the move we are about to write.
      void queryClient.cancelQueries({ queryKey }, { revert: false });
      const previousCandidates = queryClient.getQueryData<Candidate[]>(queryKey);
      writeQueryDataSync(() => {
        queryClient.setQueryData<Candidate[]>(queryKey, (current) =>
          applyOptimisticCardMove(current, move)
        );
      });
      return { previousCandidates };
    },
    [queryClient, queryKey]
  );

  const rollback = useCallback(
    (context: OptimisticContext | undefined) => {
      if (!context?.previousCandidates) return;
      queryClient.setQueryData(queryKey, context.previousCandidates);
    },
    [queryClient, queryKey]
  );

  /**
   * Reconcile once the board is quiet. A refetch triggered while another card
   * is still in flight would serve a list that predates it.
   */
  const reconcile = useCallback(() => {
    if (queryClient.isMutating({ mutationKey }) > 1) return;
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({
      queryKey: jobKeys.pipelineForJob(jobId),
    });
  }, [queryClient, queryKey, mutationKey, jobId]);

  const moveCard = useMutation({
    mutationKey,
    mutationFn: async (variables: MoveCardVariables) => {
      const accessToken = await getAccessTokenSilently();
      const response = await moveCandidatePipelineStage(
        accessToken,
        variables.candidateId,
        {
          toStageId: variables.toStageId,
          insertAfterCandidateId: variables.insertAfterCandidateId,
          // Without a drop position the backend appends to the destination stage.
          withPosition: variables.insertAfterCandidateId !== undefined,
        }
      );
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onMutate: (variables) => applyOptimisticMove(variables),
    onError: (error, variables, context) => {
      console.error('Error moving candidate stage:', error);
      rollback(context);
      applyActionFeedback(
        t,
        TOAST_PREFIX,
        feedbackForPipelineMove({
          error: feedbackErrorMessage(error),
          toStageType: variables.toStageType,
        })
      );
    },
    onSuccess: (data, variables) => {
      applyActionFeedback(
        t,
        TOAST_PREFIX,
        feedbackForPipelineMove({
          positionError: data?.position_error,
          actionsQueued: data?.actions_queued,
          toStageType: variables.toStageType,
          willNotify: variables.willNotify,
          movedToStageLabel: variables.movedToStageLabel,
        })
      );
    },
    onSettled: reconcile,
  });

  const reorderCard = useMutation({
    mutationKey,
    mutationFn: async (variables: ReorderCardVariables) => {
      const accessToken = await getAccessTokenSilently();
      const response = await updateCandidatePipelinePosition(
        accessToken,
        variables.candidateId,
        variables.insertAfterCandidateId
      );
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onMutate: (variables) =>
      applyOptimisticMove({
        candidateId: variables.candidateId,
        toStageId: variables.stageId,
        toStageType: variables.stageType,
        insertAfterCandidateId: variables.insertAfterCandidateId,
      }),
    onError: (error, _variables, context) => {
      console.error('Error reordering candidate:', error);
      rollback(context);
      const feedback = feedbackForPipelineMove({
        error: feedbackErrorMessage(error),
      });
      applyActionFeedback(
        t,
        TOAST_PREFIX,
        feedback?.key === 'invalid_status_move'
          ? { ...feedback, key: 'reorder_failed' }
          : feedback
      );
    },
    onSettled: reconcile,
  });

  return { moveCard, reorderCard };
}
